import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { ProxyGraphClients, abgGraphqlClient } from '@atlassian/temp-client-utils';
import isEmail from 'validator/lib/isEmail';
import { useFeatureToggle } from '@css-xp/base';
import {
  FetchQuotesForTransactionAccountQuery,
  FetchQuotesForTransactionAccountQueryVariables,
  SortDirection,
  QuoteStatus,
} from '@css-xp/graphtypes/src/generated-types-sxp';
import { QuoteTable } from '@css-xp/ppc-quote-table';
import { useFetchIdsFromContactEmail } from '@css-xp/contact-resources';
import { DEFAULT_PAGE_SIZE, ENABLE_DESIGN_ITERATION_1, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { searchQuotesQueryForPPC } from './queries/fetch-quotes';

interface QuoteSearchProps {
  searchTerm: string;
  transactionAccountId: string;
  partnerSearchContext?: string;
}
const defaultSortOptions = {
  sortFields: ['createdDate', 'id'],
  sortFieldsDirections: [SortDirection.DESC, SortDirection.DESC],
};
const fetchPolicy = 'cache-and-network';

export const QuotesSearch = ({ searchTerm, transactionAccountId }: QuoteSearchProps) => {
  const client = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.SXP), []);
  const [eccEmailFilter, setEccEmailFilter] = useState<{ key: string; value: string } | null>(null);
  const [isEnabled] = useFeatureToggle();
  const isSelfServeQuotingEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_SELF_SERVE_QUOTING);
  const { getResourceIdsFilterOptions } = useFetchIdsFromContactEmail({
    transactionAccountId: transactionAccountId,
  });
  const [contactEmailFilter, setContactEccEmailFilter] = useState<{ key: string; values: string[] }[] | null>(null);
  const [isEccAaidLoading, setEccAaidLoading] = useState(false);
  const isValidEmail = isEmail(searchTerm);

  const getFilterOptions = () => {
    return contactEmailFilter;
  };

  useEffect(() => {
    (async () => {
      if (isValidEmail) {
        setEccAaidLoading(true);
        const emailFilterOptions = await getResourceIdsFilterOptions(searchTerm);
        setContactEccEmailFilter(emailFilterOptions);

        setEccAaidLoading(false);
      }
    })();
  }, [searchTerm]);

  const defaultFilterOptions = {
    and: [
      {
        key: 'sourceSystem',
        value: 'CCP',
      },
      {
        key: 'transactionAccountId',
        value: transactionAccountId,
      },
    ],
    or: isValidEmail
      ? getFilterOptions()
      : [
          {
            key: 'shipToAddress1',
            value: searchTerm,
          },
          {
            key: 'quoteDescription',
            value: searchTerm,
          },
          {
            key: 'id',
            value: searchTerm,
          },
          {
            key: 'quoteNumber',
            value: searchTerm,
          },
          {
            key: 'organisationName',
            value: searchTerm,
          },
        ],
    not: [
      ...(isSelfServeQuotingEnabled
        ? // Should show DRAFT quotes in the self-serve quoting feature.
          []
        : [
            {
              key: 'status',
              value: QuoteStatus.DRAFT,
            },
          ]),
      {
        key: 'status',
        value: QuoteStatus.DELETED,
      },
    ],
  };

  // should skip the query if if `transactionAccountId` is not provided, or if the search term is a valid email and the `eccEmailFilter` is not set yet.
  const shouldSkipQuery = !transactionAccountId || (isValidEmail ? !(eccEmailFilter || contactEmailFilter) : false);

  const { data, loading, error, refetch } = useQuery<
    FetchQuotesForTransactionAccountQuery,
    FetchQuotesForTransactionAccountQueryVariables
  >(searchQuotesQueryForPPC, {
    client,
    variables: {
      searchOptions: {
        first: DEFAULT_PAGE_SIZE,
        sort: defaultSortOptions,
      },
      filterOptions: defaultFilterOptions,
    },
    skip: shouldSkipQuery,
    fetchPolicy: fetchPolicy,
    nextFetchPolicy: fetchPolicy,
  });
  const dataState = {
    loading: loading || isEccAaidLoading,
    error: error?.toString(),
    data: data?.quotes?.nodes,
    refetch: refetch,
    totalCount: data?.quotes?.totalCount ?? 0,
  };

  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  return (
    <div data-testid="quote-search" data-test-id="quote-search">
      <QuoteTable
        dataState={dataState}
        transactionAccountId={transactionAccountId}
        shouldHideTabs={true}
        shouldHideActionsMenu={true}
        defaultSearchFilterOptions={defaultFilterOptions}
        label={'Quotes'}
        shouldHideFilters={isDesignIteration1Enabled}
        isSearchTermPresent={!!searchTerm}
      />
    </div>
  );
};

QuotesSearch.displayName = 'QuotesSearch';
